import type { UserManagementApi } from '@liks/backend-svc-api/esm/apis/UserManagementApi';
import type { UserLabel } from '@liks/backend-svc-api/esm/models/index';
import type { WithResourceId } from '../types/knowledge';
import {
  hasNamedResourceId,
  toAssignedDirectoryUser,
} from './userServiceGuards';
import type { AssignedDirectoryUser } from './userServiceTypes';

export async function listLabels(
  userApi: UserManagementApi,
): Promise<Array<WithResourceId<UserLabel> & { name: string }>> {
  const labels =
    await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceListLabels();
  return labels.filter(hasNamedResourceId);
}

export async function createLabel(
  userApi: UserManagementApi,
  name: string,
): Promise<UserLabel> {
  return userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceCreateLabel({
    userLabel: { name },
  });
}

export async function renameLabel(
  userApi: UserManagementApi,
  labelId: string,
  name: string,
): Promise<UserLabel> {
  return userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceRenameLabel({
    labelId,
    userLabelRenameRequest: { name },
  });
}

export async function deleteLabel(
  userApi: UserManagementApi,
  labelId: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceDeleteLabel({
    labelId,
  });
}

export async function listUserLabels(
  userApi: UserManagementApi,
  userId: string,
): Promise<Array<WithResourceId<UserLabel> & { name: string }>> {
  const labels =
    await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceListUserLabels(
      { userId },
    );
  return labels.filter(hasNamedResourceId);
}

export async function listLabelUsers(
  userApi: UserManagementApi,
  labelId: string,
): Promise<AssignedDirectoryUser[]> {
  const users =
    await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceListLabelUsers(
      { labelId },
    );
  return users
    .map(toAssignedDirectoryUser)
    .filter((user): user is AssignedDirectoryUser => user != null);
}

export async function assignUserLabel(
  userApi: UserManagementApi,
  userId: string,
  labelId: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceAssignUserLabel(
    { userId, labelId },
  );
}

export async function unassignUserLabel(
  userApi: UserManagementApi,
  userId: string,
  labelId: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceUnassignUserLabel(
    { userId, labelId },
  );
}
